<template>
  <ComboboxRoot
    v-model="selectedItem"
    v-model:open="isOpen"
    openOnFocus
    class="Autocomplete"
    :class="[parentClass, {
      'is-disabled': disabled,
      'is-error': hasError,
      'is-static': static,
      'is-filled': !!selectedItem,
    }]"
    :disabled="disabled || static"
    highlightOnHover
    ignoreFilter
  >
    <FieldLabel
      v-if="label || $slots.label"
      :for="name"
      :required="isRequired"
      :hideAsterisk
      :disabled
    >
      <slot name="label">
        {{ label }}
      </slot>
    </FieldLabel>

    <ComboboxAnchor class="Autocomplete-field">
      <div class="Autocomplete-fieldInner">
        <div v-if="leftIcon" class="Autocomplete-searchIcon">
          <Icon :icon="leftIcon" :size="20" />
        </div>

        <ComboboxInput
          :id="name"
          v-bind="attrs"
          v-model="searchTerm"
          class="Autocomplete-input"
          :placeholder
          :disabled
          :readonly="static"
          :name
          :displayValue="(value: AutocompleteOption) => value?.label || ''"
        />

        <ComboboxCancel
          v-if="clearable && selectedItem && !disabled"
          class="Autocomplete-clearButton"
          @click="onClear"
        >
          <Icon icon="window-close" :size="20" />
        </ComboboxCancel>
      </div>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        class="Autocomplete-dropdown"
        :sideOffset
        align="start"
        position="popper"
      >
        <div class="Autocomplete-dropdownInner">
          <slot name="prepend" />

          <ComboboxEmpty v-if="filteredFlatOptions.length === 0" class="Autocomplete-empty">
            <DropdownItem text="No results found" disabled />
          </ComboboxEmpty>

          <template v-for="option of filteredOptions" :key="option.label">
            <ComboboxGroup v-if="isAutocompleteGroup(option)">
              <ComboboxItem
                class="Autocomplete-groupHeader"
                :value="option"
                :disabled="option.disabled"
              >
                <span class="Autocomplete-groupHeaderLabel">{{ option.label }}</span>
                <span v-if="option.secondaryLabel" class="Autocomplete-groupHeaderSecondary">- {{ option.secondaryLabel }}</span>
              </ComboboxItem>

              <ComboboxItem
                v-for="item of option.items"
                :key="item.label"
                class="Autocomplete-groupOption"
                :value="item"
                :disabled="item.disabled"
              >
                <div class="Autocomplete-groupOptionInner">
                  <span class="Autocomplete-groupOptionLabel">{{ item.label }}</span>
                </div>
              </ComboboxItem>
            </ComboboxGroup>

            <ComboboxItem
              v-else
              :value="option"
              :disabled="option.disabled"
              asChild
            >
              <DropdownItem
                :leftIcon="option.leftIcon"
                :rightIcon="option.rightIcon"
                :text="option.label"
              />
            </ComboboxItem>
          </template>
        </div>
      </ComboboxContent>
    </ComboboxPortal>

    <select
      v-if="autocomplete"
      class="u-visually-hidden"
      :name="name"
      :autocomplete="autocomplete"
      tabindex="-1"
      aria-hidden="true"
      @input="onAutofill"
    >
      <option value="" />
      <option
        v-for="option of flatOptions"
        :key="String(option.value)"
        :value="String(option.value)"
      >
        {{ option.label }}
      </option>
    </select>

    <FieldHelper
      :errorMessage="errorMessage"
      :helperText="helperText"
    />
  </ComboboxRoot>
</template>

<script setup lang="ts" generic="T extends AcceptableValue = string">
import { computed, ref, shallowRef, useAttrs, watch } from 'vue';
import {
  ComboboxRoot,
  ComboboxAnchor,
  ComboboxInput,
  ComboboxPortal,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxCancel,
  ComboboxGroup,
  useFilter,
  type AcceptableValue,
} from 'reka-ui';
import { useFieldValidation } from '@cvo/ui/composables';
import FieldLabel from '../../atoms/FieldLabel/FieldLabel.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import DropdownItem from '../Dropdown/DropdownItem.vue';
import FieldHelper from '../../atoms/FieldHelper/FieldHelper.vue';
import { isAutocompleteGroup, type AutocompleteProps, type AutocompleteOption, type AutocompleteGroupOption } from './Autocomplete.model';

const props = withDefaults(defineProps<AutocompleteProps<T>>(), {
  placeholder: 'Search...',
  error: undefined,
  sideOffset: 4,
  options: () => [] as Array<AutocompleteOption<T> | AutocompleteGroupOption<T>>,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: T | undefined): void;
}>();

defineOptions({ inheritAttrs: false });

const { class: parentClass, ...attrs } = useAttrs();

// Internal state for uncontrolled mode (no v-model). Seeded from initialValue at setup;
// updated reactively when a matching option arrives asynchronously (e.g. after routes load).
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const internalValue = shallowRef<AutocompleteOption<T> | undefined>(props.initialValue);
watch(
  () => props.initialValue,
  (newVal) => {
    // Only adopt the incoming option when it matches the current modelValue, so we never
    // stomp a selection the user has already made.
    if (newVal?.value === props.modelValue) {
      internalValue.value = newVal;
    }
  },
);
const isOpen = ref(false);
const searchTerm = ref('');

const { contains } = useFilter({ sensitivity: 'base' });

function flattenOptions(opts: Array<AutocompleteOption<T> | AutocompleteGroupOption<T>>): Array<AutocompleteOption<T>> {
  return opts.flatMap((opt): Array<AutocompleteOption<T>> => {
    if (isAutocompleteGroup(opt)) {
      return [opt, ...opt.items];
    }
    return [opt];
  });
}

const flatOptions = computed<Array<AutocompleteOption<T>>>(() => flattenOptions(props.options));

const skipFiltering = computed(() => {
    const searchTermIsCurrentSelection = selectedItem.value != null && searchTerm.value === selectedItem.value.label;
    return !searchTerm.value || searchTermIsCurrentSelection;
});

// Filtering is handled manually (ComboboxRoot's `ignoreFilter`) instead of Reka's default per-item text filtering,
// so that groups behave as follows:
// - If the group header label matches, show the header with all of its items.
// - Else if only some items match, show the header with only those matching items.
// - Else exclude the group entirely.
const filteredOptions = computed<Array<AutocompleteOption<T> | AutocompleteGroupOption<T>>>(() => {
  if (skipFiltering.value) return props.options;

  return props.options.flatMap((option): Array<AutocompleteOption<T> | AutocompleteGroupOption<T>> => {
    if (!isAutocompleteGroup(option)) {
      return contains(option.label, searchTerm.value) ? [option] : [];
    }

    if (contains(option.label, searchTerm.value)) return [option];

    const matchingItems = option.items.filter(item => contains(item.label, searchTerm.value));
    return matchingItems.length > 0 ? [{ ...option, items: matchingItems }] : [];
  });
});

const filteredFlatOptions = computed<Array<AutocompleteOption<T>>>(() => flattenOptions(filteredOptions.value));

const { hasError, errorMessage, isRequired, hideAsterisk } = useFieldValidation(props);

const selectedItem = computed<AutocompleteOption<T> | undefined>({
  get: () => {
    if (props.modelValue === internalValue.value?.value) {
      return internalValue.value;
    }
    return flatOptions.value.find(option => option.value === props.modelValue);
  },
  set: (newSelectedItem) => {
    internalValue.value = newSelectedItem;
    emit('update:modelValue', newSelectedItem?.value);
  },
});

// Clear the selected item when the user remove all search term
watch(searchTerm, (newSearchTerm, oldSearchTerm) => {
  if (newSearchTerm === '' && oldSearchTerm !== '') {
    selectedItem.value = undefined;
  }
});

function onClear() {
  selectedItem.value = undefined;
  searchTerm.value = '';
  isOpen.value = true;
}

function onAutofill(event: Event) {
  const val = (event.target as HTMLSelectElement).value;
  if (!val) {
    selectedItem.value = undefined;
    return;
  }
  const match = flatOptions.value.find(opt => String(opt.value) === val);
  selectedItem.value = match ?? undefined;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Autocomplete {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;
    width: 100%;
    max-width: 800px;
    min-width: 64px;
    position: relative;

    // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
    --field-label-color: #{vars.$atoms-autocomplete-baseinput-enabled-label-color};

    // --- Field (Input Container) Styling ---
    &-field {
      position: relative;
      border-radius: vars.$atoms-autocomplete-baseinput-border-radius;
      border-width: vars.$atoms-autocomplete-baseinput-border-stroke;
      border-style: solid;

      // Default / Enabled
      background-color: vars.$atoms-autocomplete-baseinput-enabled-bg-color;
      border-color: vars.$atoms-autocomplete-baseinput-enabled-border-color;
      padding: vars.$atoms-autocomplete-baseinput-padding-top-bottom vars.$atoms-autocomplete-baseinput-padding-left-right;
      min-height: 29px;
      transition: border-color 0.2s ease, background-color 0.2s ease;
      box-sizing: border-box;
    }

    // --- Inner ---
    &-fieldInner {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
      height: 100%;
      width: 100%;
    }

    // --- Icon ---
    &-searchIcon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: vars.$atoms-autocomplete-baseinput-enabled-icon-color;
    }

    // --- Input ---
    &-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      @include text.text(sm);
      color: vars.$atoms-autocomplete-baseinput-text-color;
      min-height: 20px;
      min-width: 0;

      &::placeholder {
        color: vars.$atoms-autocomplete-baseinput-placeholder-color;
      }
    }

    &-clearButton {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      border: none;
      background: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: vars.$atoms-autocomplete-baseinput-enabled-icon-color;
      padding: 0;

      &:hover {
        color: vars.$atoms-autocomplete-baseitem-hover-icon-color;
      }
    }

    .FieldHelper {
       color: vars.$atoms-autocomplete-baseinput-enabled-helper-color;

       &.is-error {
          color: vars.$atoms-autocomplete-baseinput-error-helper-color;
       }
    }

    // --- State overrides ---
    &.is-filled {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-autocomplete-baseinput-filled-label-color};
      .Autocomplete-field {
        background-color: vars.$atoms-autocomplete-baseinput-filled-bg-color;
        border-color: vars.$atoms-autocomplete-baseinput-filled-border-color;
      }
      .Autocomplete-searchIcon {
        color: vars.$atoms-autocomplete-baseinput-filled-icon-color;
      }
      .FieldHelper:not(.is-error) {
         color: vars.$atoms-autocomplete-baseinput-filled-helper-color;
      }
    }

    &:focus-within {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-autocomplete-baseinput-focused-label-color};
      .Autocomplete-field {
        background-color: vars.$atoms-autocomplete-baseinput-focused-bg-color;
        border-color: vars.$atoms-autocomplete-baseinput-focused-border-color;
      }
      .Autocomplete-searchIcon {
        color: vars.$atoms-autocomplete-baseinput-focused-icon-color;
      }
      .FieldHelper:not(.is-error) {
         color: vars.$atoms-autocomplete-baseinput-focused-helper-color;
      }
    }

    &.is-error {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-autocomplete-baseinput-error-label-color};
      .Autocomplete-field {
        background-color: vars.$atoms-autocomplete-baseinput-error-bg-color;
        border-color: vars.$atoms-autocomplete-baseinput-error-border-color;
      }
      .Autocomplete-searchIcon {
        color: vars.$atoms-autocomplete-baseinput-error-icon-color;
      }
    }

    &.is-disabled {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-autocomplete-baseinput-disabled-label-color};

      .Autocomplete-field {
        background-color: vars.$atoms-autocomplete-baseinput-disabled-bg-color;
        border-color: vars.$atoms-autocomplete-baseinput-disabled-border-color;
        cursor: not-allowed;

        input {
          pointer-events: none;
        }
      }
      .Autocomplete-searchIcon {
        color: vars.$atoms-autocomplete-baseinput-disabled-icon-color;
      }
      .FieldHelper {
         color: vars.$atoms-autocomplete-baseinput-disabled-helper-color;
      }

      &.is-error {
        .Autocomplete-field {
          border-color: vars.$atoms-autocomplete-baseinput-error-border-color;
        }
        .FieldHelper {
          color: vars.$atoms-autocomplete-baseinput-error-helper-color;
        }
      }
    }

     &.is-static {
      --field-label-color: #{vars.$atoms-autocomplete-baseinput-static-label-color};

      .Autocomplete-field {
        background-color: vars.$atoms-autocomplete-baseinput-static-bg-color;
        border-color: vars.$atoms-autocomplete-baseinput-static-border-color;

        input {
          pointer-events: none;
        }
      }
      .Autocomplete-searchIcon {
        color: vars.$atoms-autocomplete-baseinput-static-icon-color;
      }
      .FieldHelper {
         color: vars.$atoms-autocomplete-baseinput-static-helper-color;
      }
    }

    &-dropdown {
      transform-origin: var(--reka-popper-transform-origin);
      animation: scaleIn vars.$transition-fast ease forwards;
      background-color: vars.$atoms-autocomplete-baseitem-default-bg-color;
      border-radius: vars.$border-radius-sm;
      box-shadow: 0px 2px 8px 0px rgba(51, 51, 51, 0.15);
      border: none;
      overflow: hidden;
      z-index: 50;
      min-width: var(--reka-combobox-trigger-width);
      max-height: 200px;
      overflow-y: auto;

      &Inner {
        display: flex;
        width: var(--reka-combobox-trigger-width);
        flex-direction: column;
      }

      .DropdownItem {
        background-color: vars.$atoms-autocomplete-baseitem-default-bg-color;
        &[data-highlighted] {
          color: vars.$atoms-autocomplete-baseitem-hover-label-color;
          background-color: vars.$atoms-autocomplete-baseitem-hover-bg-color;
        }
      }
    }

    &-empty {
      padding: 0;
    }

    &-groupHeader {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      padding: vars.$atoms-autocomplete-baseitem-padding-top-bottom vars.$atoms-autocomplete-baseitem-padding-left-right;
      border-radius: vars.$atoms-autocomplete-baseitem-border-radius;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &[data-highlighted]:not([data-disabled]) {
        background-color: vars.$atoms-autocomplete-baseitem-hover-bg-color;
        border-radius: vars.$atoms-autocomplete-baseitem-border-radius vars.$atoms-autocomplete-baseitem-border-radius 0 0;

        .Autocomplete-groupHeaderLabel {
          color: vars.$atoms-autocomplete-baseitem-hover-label-color;
        }

        .Autocomplete-groupHeaderSecondary {
          color: vars.$atoms-autocomplete-baseitem-hover-secondarytext-color;
        }
      }

      &[data-state='checked']:not([data-disabled]) {
        background-color: vars.$atoms-autocomplete-baseitem-active-bg-color;

        .Autocomplete-groupHeaderLabel {
          color: vars.$atoms-autocomplete-baseitem-active-label-color;
        }

        .Autocomplete-groupHeaderSecondary {
          color: vars.$atoms-autocomplete-baseitem-active-secondarytext-color;
        }
      }

      &[data-disabled] {
        cursor: default;
      }

      &Label {
        @include text.text(md, vars.$typography-weight-strong);
        color: vars.$atoms-autocomplete-baseitem-default-label-color;
      }

      &Secondary {
        @include text.text(xs);
        color: vars.$atoms-autocomplete-baseitem-default-secondarytext-color;
      }
    }

    // When the group header is highlighted (and not disabled), propagate hover style to all sibling child options
    &-groupHeader[data-highlighted]:not([data-disabled]) ~ &-groupOption {
      background-color: vars.$atoms-autocomplete-baseitem-hover-bg-color;
      color: vars.$atoms-autocomplete-baseitem-hover-optionbreakdown-color;
      border-radius: 0;
      &:last-child {
        border-radius: 0 0 vars.$atoms-autocomplete-baseitem-border-radius vars.$atoms-autocomplete-baseitem-border-radius;
      }
    }

    &-groupOption {
      border-radius: vars.$atoms-autocomplete-baseitem-border-radius;
      cursor: pointer;
      transition: background-color 0.2s ease;
      color: vars.$atoms-autocomplete-baseitem-default-optionbreakdown-color;

      &[data-highlighted] {
        background-color: vars.$atoms-autocomplete-baseitem-hover-bg-color;
        color: vars.$atoms-autocomplete-baseitem-hover-optionbreakdown-color;
      }

      &[data-state='checked'] {
        background-color: vars.$atoms-autocomplete-baseitem-active-bg-color;
        color: vars.$atoms-autocomplete-baseitem-active-optionbreakdown-color;
      }

      &[data-disabled] {
        color: vars.$atoms-autocomplete-baseitem-disabled-optionbreakdown-color;
        cursor: not-allowed;
      }

      &Inner {
        display: flex;
        align-items: center;
        gap: vars.$dimensions-d-2;
        margin-inline-start: vars.$atoms-autocomplete-baseitem-padding-left-right;
        border-inline-start: vars.$dimensions-d-0-25 solid vars.$color-neutral-gray-300;
        padding-inline-start: vars.$dimensions-d-2;
        padding-block: vars.$atoms-autocomplete-baseitem-padding-top-bottom;
        padding-inline-end: vars.$atoms-autocomplete-baseitem-padding-left-right;
      }

      &Label {
        @include text.text(sm);
      }
    }
  }
}
</style>
